// web/src/components/loader/UnicornBeam.tsx
// Full-bleed Unicorn Studio WebGL background (star + charging beam + swirl + vignette).
// A throttled rAF loop pushes charge-derived values into the scene's wired variables via
// the wrapper's sceneRef. charge is read from a ref so the loop never triggers re-renders.
//
// Scene source = a SELF-HOSTED copy of the exported scene JSON (web/public/unicorn/
// roast-loader.json), NOT the hosted `projectId`. This makes the committed file the single
// source of truth — no publish step, no 6-month Unicorn CDN cache. To update the scene:
// export the JSON from Unicorn Studio and overwrite that file (its variable/binding
// display-names must stay in sync with lib/loaderBeam VAR_NAMES).
import { useEffect, useRef } from "react";
import type { JSX } from "react";
import { useReducedMotion } from "motion/react";
import UnicornScene from "unicornstudio-react";
import { beamVarsForCharge, useLoaderFx } from "@/lib/loaderBeam";

// SELF-HOSTED copy of the v2.2.6 UMD build (web/public/unicorn/), matching the
// self-hosted scene JSON below — no runtime jsDelivr dependency (pre-deploy audit:
// an unpinned-integrity third-party script in a signed-in origin is a supply-chain
// risk, and a CDN outage would break the loader). To upgrade: download the new
// dist/unicornStudio.umd.js from the unicornstudio.js release tag into public/unicorn/.
const SDK_URL = "/unicorn/unicornStudio-v2.2.6.umd.js";
const SCENE_JSON = "/unicorn/roast-loader.json"; // served from web/public
const THROTTLE_MS = 66; // ~15 var-updates/sec — the scene animates at 60fps regardless

export function UnicornBeam({ charge, onReady }: { charge: number; onReady?: () => void }): JSX.Element {
  // Scene instance handed back by the wrapper once loaded (carries setVariables).
  const sceneRef = useRef<{
    setVariables?: (v: Record<string, number>) => void;
    getVariableDefinitions?: () => unknown;
    getVariables?: () => unknown;
    paused?: boolean;
  } | null>(null);
  const chargeRef = useRef(charge);
  chargeRef.current = charge; // updated every render without re-subscribing the loop
  const fx = useLoaderFx();
  const reduced = useReducedMotion();

  useEffect(() => {
    // The wrapper populates sceneRef.current ASYNCHRONOUSLY (after this effect runs), so
    // pausing here would be a no-op — the reduced-motion pause is wired via onLoad instead.
    if (reduced) return;
    let raf = 0;
    let last = 0;
    const loop = (t: number): void => {
      raf = requestAnimationFrame(loop);
      if (t - last < THROTTLE_MS) return;
      last = t;
      sceneRef.current?.setVariables?.(beamVarsForCharge(chargeRef.current, fx));
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reduced, fx]);

  return (
    <UnicornScene
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sceneRef={sceneRef as any}
      jsonFilePath={SCENE_JSON}
      sdkUrl={SDK_URL}
      width="100%"
      height="100%"
      dpi={1.5}
      scale={1}
      className="absolute inset-0 h-full w-full"
      // Declarative, wrapper-supported pause — holds a still frame for reduced-motion users
      // without racing the async sceneRef.
      paused={!!reduced}
      // onReady lets the parent hold this layer at opacity-0 until the scene has actually
      // rendered (LoaderScene's entrance sequencing) — no black-canvas / mid-morph pop.
      // DEV diagnostic: dump the scene's real variable names once loaded, so we can confirm
      // exactly which var is bound to what (e.g. the swirl-speed binding).
      onLoad={() => {
        onReady?.();
        if (!import.meta.env.DEV) return;
        const s = sceneRef.current;
        // eslint-disable-next-line no-console
        console.log("[loader] unicorn variables:", s?.getVariableDefinitions?.() ?? s?.getVariables?.() ?? "(no variable API on scene)");
      }}
    />
  );
}
